// Lot 630 — Mode hors ligne : commentaires, réactions (publications / commentaires) et cœur des Reels envoyés sans réseau
// sont mis en file SUR L'APPAREIL (localStorage) et renvoyés automatiquement au retour du réseau. Les appels sont
// idempotents côté serveur (client_id sur les commentaires — lot 628 ; une réaction = un état), donc rejouables sans doublon.
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { isOffline } from "@/lib/netStatus";
import { readCachedUser } from "@/lib/offlineCache";
import { guardOn } from "@/lib/guards";

const KEY = "yalola.offlineQueue";
const EVT = "yalola:offline-queue";          // la file a changé → { items }
export const EVT_FLUSHED = "yalola:offline-flushed"; // un élément a été envoyé → { item, data }
export const EVT_FAILED = "yalola:offline-failed";   // refusé par le serveur (4xx) → { item, message }
export const EVT_FLUSH_DONE = "yalola:offline-flush-done"; // fin d'un passage → { sent, failed }
const RETRY_MS = 30000;
const MAX_AGE_MS = 3 * 24 * 3600 * 1000;
const MAX_ITEMS = 200;

const newId = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `q-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
const emit = (name, detail) => { try { window.dispatchEvent(new CustomEvent(name, { detail })); } catch { /* noop */ } };

export const readOfflineQueue = () => {
  try { const a = JSON.parse(localStorage.getItem(KEY) || "[]"); return Array.isArray(a) ? a : []; } catch { return []; }
};
const write = (items) => {
  try { items.length ? localStorage.setItem(KEY, JSON.stringify(items.slice(-MAX_ITEMS))) : localStorage.removeItem(KEY); } catch { /* quota */ }
  emit(EVT, { items });
};

// Erreur réseau (aucune réponse du serveur) ou appareil hors ligne → l'action est à mettre en file plutôt qu'à abandonner.
// Lot 671 — garde-fou admin « offline_queue » coupé → jamais de mise en file : l'appelant suit son chemin d'erreur normal.
export const shouldQueue = (e) =>
  guardOn("offline_queue") && (isOffline() || (!!e && !e.response && (e.code === "ERR_NETWORK" || e.code === "ECONNABORTED" || /network/i.test(String(e.message || "")))));

// `key` identifie la cible (post:<id>, comment:<id>) : une nouvelle réaction sur la même cible REMPLACE l'ancienne en file
// (seul le dernier choix compte). Les commentaires (key null) s'ajoutent tous, dans l'ordre.
export const enqueueOffline = ({ kind, method, url, body = null, key = null, meta = {} }) => {
  const user = readCachedUser();
  if (body && typeof body === "object" && kind === "comment" && !body.client_id) body.client_id = newId();
  const item = { id: newId(), kind, method: String(method || "post").toLowerCase(), url, body, key, meta, user_id: user?.id || null, at: Date.now() };
  const rest = key ? readOfflineQueue().filter((x) => x.key !== key) : readOfflineQueue();
  write([...rest, item]);
  return item;
};

export const dequeueOffline = (id) => write(readOfflineQueue().filter((x) => x.id !== id));
export const queuedFor = (key) => readOfflineQueue().find((x) => x.key === key) || null;

let flushing = false;
let timer = null;

export const flushOfflineQueue = async () => {
  if (flushing || typeof window === "undefined" || isOffline()) return { sent: 0, failed: 0 };
  flushing = true;
  let sent = 0, failed = 0;
  try {
    const me = readCachedUser();
    for (const item of readOfflineQueue()) {
      if ((me?.id && item.user_id && item.user_id !== me.id) || Date.now() - (item.at || 0) > MAX_AGE_MS) { dequeueOffline(item.id); continue; }
      try {
        const { data } = await api.request({ method: item.method, url: item.url, data: item.body ?? undefined, timeout: 30000 });
        dequeueOffline(item.id);
        sent += 1;
        emit(EVT_FLUSHED, { item, data });
      } catch (e) {
        if (shouldQueue(e)) break; // toujours hors ligne → on réessaiera plus tard, l'ordre est conservé
        dequeueOffline(item.id);
        failed += 1;
        emit(EVT_FAILED, { item, message: e?.friendlyMessage || e?.response?.data?.detail || "" });
      }
    }
  } finally {
    flushing = false;
  }
  if (sent || failed) emit(EVT_FLUSH_DONE, { sent, failed });
  return { sent, failed };
};

const schedule = (ms = 800) => { clearTimeout(timer); timer = setTimeout(() => { flushOfflineQueue(); }, ms); };

// index.js : écoute le retour du réseau (web + natif), la 1re réponse serveur réussie et réessaie toutes les 30 s tant
// qu'il reste quelque chose en file.
export const installOfflineQueue = () => {
  if (typeof window === "undefined" || window.__yalolaOfflineQueue) return;
  window.__yalolaOfflineQueue = true;
  window.addEventListener("online", () => schedule(500));
  window.addEventListener("yalola:network-online", () => schedule(500));
  window.addEventListener("yalola:server-ok", () => { if (readOfflineQueue().length) schedule(300); });
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible" && readOfflineQueue().length) schedule(500); });
  setInterval(() => { if (readOfflineQueue().length) flushOfflineQueue(); }, RETRY_MS);
  if (readOfflineQueue().length) schedule(3000);
};

export const useOfflineQueue = () => {
  const [items, setItems] = useState(readOfflineQueue);
  useEffect(() => {
    const on = (e) => setItems(e?.detail?.items || readOfflineQueue());
    window.addEventListener(EVT, on);
    return () => window.removeEventListener(EVT, on);
  }, []);
  return items;
};

// Vrai tant qu'une action est en attente pour cette cible (post:<id>, comment:<id>) → petit indicateur « En attente du réseau ».
export const useQueuedOffline = (key) => {
  const items = useOfflineQueue();
  return !!key && items.some((x) => x.key === key);
};
